import React, {Dispatch} from "react";
import Markdown from 'react-markdown'
import {Circle} from "rc-progress";
import Uploady, {useRequestPreSend, useItemProgressListener, useItemFinishListener} from '@rpldy/uploady';
import UploadDropZone from "@rpldy/upload-drop-zone";
import UploadButton from '@rpldy/upload-button';

import './Upload.css';
import SectionHandler from "./SectionHandler";
import DetailsSection from "./DetailsSection";
import rehypeExternalLinks from "rehype-external-links";
import {ActionPageCreate, StatePageCreate} from "../PageCreate";

import {
    MDXEditor,
    UndoRedo,
    BoldItalicUnderlineToggles,
    toolbarPlugin,
    headingsPlugin,
    quotePlugin,
    listsPlugin,
    linkPlugin,
    CreateLink, ListsToggle, BlockTypeSelect, linkDialogPlugin
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'

type CallbackFunction = () => void;
const UploadProgress = (props: { state: StatePageCreate, dispatch: Dispatch<ActionPageCreate> }) => {
    const progressData = useItemProgressListener() || {completed: 0};
    const completed = progressData.completed;
    const finishItem = useItemFinishListener(async (item) => {
        console.log(`item ${item.id} finished uploading, response was: `, item);
        console.log('progressData', progressData);
        console.log('completed', completed);
        let file = item.file;
        let {name, type} = file;

        // @ts-ignore
        let {imageUuid} = file['userData'];
        let url = `/imager/create.json?` +
            new URLSearchParams({
                name,
                type,
                imageUuid
            });
        const fetch_options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        };

        const response = await fetch(url, fetch_options);
        const responseJson = await response.json()

        console.log('UploadProgress RESPONSE', response);
        console.log('responseJson', responseJson);
        // let uploadUrl = (await response.json()).upload_url;

        props.dispatch({type: 'toggle-set', payload: 'image-upload-loaded', value: false})
        props.dispatch({type: 'toggle-set', payload: 'image-upload-in-progress', value: false})
        props.dispatch({type: "image-uploaded", url: responseJson.image_url, uuid: responseJson.image_uuid});

        return {success: true};
    });

    let imageLoaded = props.state.toggles.has('image-upload-loaded');
    let imageInProgress = props.state.toggles.has('image-upload-in-progress');

    let showProgress = imageInProgress || !(completed === 0 || (completed === 100 && imageLoaded));

    // conditional rendering breaks progress updating for some reason
    return <div className={"flex flex-row-reverse mb-2 h-25"}>
        {showProgress && <Circle
            className="progress-circle"
            strokeWidth={5}
            strokeColor={completed === 100 ? "#4dc14d" : "#40affd"}
            percent={completed}/>
        }
        {showProgress &&
            <span className="px-2 py-2 text-sm text-gray-700">
                {completed === 100 ? "Upload complete. Resizing..." : "Uploading..."}
            </span>
        }
        {!showProgress &&
            <span className="block text-gray-600 mb-4 upload-button-link">
                      Upload or drag and drop a new image
            </span>
        }
    </div>;
}

const SignedUploadDragAndDrop = (props: { state: StatePageCreate, dispatch: Dispatch<ActionPageCreate> }) => {
    useRequestPreSend(async ({items, options}) => {
        props.dispatch({type: 'toggle-set', payload: 'image-upload-loaded', value: false})
        props.dispatch({type: 'toggle-set', payload: 'image-upload-in-progress', value: true})
        const files = items.length > 0 ? items[0] : {};

        // @ts-ignore
        let {file} = files;
        let {name, type} = file;
        let url: string = `/imager/new.json?` + new URLSearchParams({name, type,});
        const fetch_options = {headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}};

        const response = await fetch(url, fetch_options);
        const responseJson = await response.json()

        console.log('useRequestPreSend RESPONSE', responseJson);
        props.dispatch({type: 'toggle-set', payload: 'image-upload-loaded', value: false})
        let uploadUrl = responseJson.upload_url;
        let imageUuid = responseJson.image_uuid;
        file.userData = {imageUuid: imageUuid};

        return {
            options: {
                sendWithFormData: false,
                destination: {
                    url: uploadUrl,
                    method: 'PUT',
                    headers: {
                        'Content-Type': type
                    },
                },
                userData: {
                    imageUuid: imageUuid
                }
            },
        };
    });

    const handleImageLoaded = (event: any) => {
        console.log("IMAGE LOADED", event)
        props.dispatch({type: 'toggle-set', payload: 'image-upload-loaded', value: true})

    }

    const imageTag = () => {
        if (!props.state.imageUrl) {
            return <></>
        }
        let imageLoaded = props.state.toggles.has('image-upload-loaded');
        let imageInProgress = props.state.toggles.has('image-upload-in-progress');
        let showProgress = imageInProgress || !imageLoaded;
        return <>
            <div className="image-container">
                <img src={props.state.imageUrl} onLoad={handleImageLoaded.bind(this)}
                     className={"center rounded loading"}/>
                {
                    ((props.state.toggles.has('generated') && props.state.imagerSample.isInProgress()) || showProgress) &&
                    <img src="/images/spin.svg" className="overlay-spinner"/>
                }
            </div>
        </>
    }

    return (<UploadDropZone
        className="drag-and-drop border-dashed border-2 w-64 h-32 rounded justify-center items-center flex flex-col"
        onDragOverClassName="drag-over">
        <UploadButton className="upload-button-plain">
            {imageTag()}
            <UploadProgress state={props.state} dispatch={props.dispatch}/>
        </UploadButton>
    </UploadDropZone>);
};

class UploadSection extends SectionHandler {
    nextHandler(): SectionHandler | null {
        return this.state.toggles.has('image-complete') ? new DetailsSection(this.state, this.dispatch) : null;
    }

    editTextSection() {
        return <>
            <div className="row mt-4 justify-content-md-center">
                <div className="col-lg-8 page-create-editor">
                    <MDXEditor
                        ref={this.state.editorReference}
                        markdown={this.state.caption}
                        onChange={(e) => {
                            this.dispatch({type: "set-caption", payload: e})
                        }}
                        plugins={[
                            headingsPlugin(), listsPlugin(), quotePlugin(), linkPlugin(), linkDialogPlugin(),
                            toolbarPlugin({
                                toolbarContents: () => (
                                    <>
                                        {' '}
                                        <UndoRedo/>
                                        <BoldItalicUnderlineToggles/>
                                        <CreateLink/>
                                        <ListsToggle options={[ 'bullet', 'number' ]}/>
                                        <BlockTypeSelect/>
                                    </>
                                )
                            })
                        ]}/>
                </div>
            </div>
            <div className="row my-3">
                <div className="md-12 text-center">
                    <button className={"btn btn-primary btn-lg"}
                            onClick={() => this.dispatch({type: 'toggle', payload: 'image-complete'})}>
                        Continue
                    </button>
                </div>
            </div>
        </>
    }

    displayTextSection() {
        return <>
            <div className="row mt-4 justify-content-md-center">
                <div className="col-lg-8">
                    <Markdown rehypePlugins={[[rehypeExternalLinks, {target: '_blank'}]]}>
                        {this.state.caption}
                    </Markdown>
                </div>
            </div>
            <div className="row my-3">
                <div className="md-12 text-center">
                    <button className={"btn btn-secondary btn-lg"}
                            onClick={() => this.dispatch({type: 'toggle', payload: 'image-complete'})}>
                        Edit
                    </button>
                </div>
            </div>
        </>
    }

    handle() {
        return (
            <>
                <div className="row mt-4">
                    <div className="md-12 text-center">
                        <div className="upload-container">
                            <Uploady destination={{}}>
                                <SignedUploadDragAndDrop state={this.state} dispatch={this.dispatch}/>
                            </Uploady>
                        </div>
                    </div>
                </div>
                {this.state.toggles.has('image-complete') ? this.displayTextSection() : this.editTextSection()}
            </>
        );
    }
}


export default UploadSection;
